import { ClinicalMeasurement } from "../types";
import { PatientAgeInfo } from "../types";

export const SCREENER_MIN_AGE = 12;
export const PHQ9_DIAGNOSIS_CODES = [
  "F32A",
  "F320",
  "F321",
  "F322",
  "F3289",
  "F323",
  "F330",
  "F331",
  "F332",
  "F333",
  "F338",
  "F339",
];
export const GAD7_DIAGNOSIS_CODES = [
  "F400",
  "F4001",
  "F4002",
  "F401",
  "F4010",
  "F402",
  "F410",
  "F4101",
  "F411",
  "F419",
  "F420",
  "F430",
  "F431",
  "F930",
  "F940",
];

export const isScreenerApplicable = (
  patientAge: PatientAgeInfo | null | undefined,
  diagnosisCodes: string | null | undefined,
  validCodes: string[],
): boolean => {
  if (!patientAge || !diagnosisCodes) {
    return false;
  }

  const hasValidAge = patientAge.years >= SCREENER_MIN_AGE;

  // Check if any of the valid diagnosis codes are present in the patient's diagnosisCodes string
  const hasValidDiagnosis = validCodes.some((code) =>
    diagnosisCodes.includes(code),
  );

  return hasValidAge && hasValidDiagnosis;
};

export const computeScreenerNextDueDate = (
  lastCompletedStr: string | null | undefined,
  latestScore: number,
): string => {
  if (!lastCompletedStr || lastCompletedStr === "N/A") {
    return "N/A";
  }

  const lastCompleted = new Date(lastCompletedStr);
  if (isNaN(lastCompleted.getTime())) {
    return "N/A";
  }

  const nextDue = new Date(lastCompleted.getTime());

  if (latestScore >= 15) {
    nextDue.setDate(nextDue.getDate() + 14); // 2 weeks
  } else if (latestScore >= 5) {
    nextDue.setMonth(nextDue.getMonth() + 1); // 1 month
  } else {
    nextDue.setMonth(nextDue.getMonth() + 3); // 90 days (approx 3 months)
  }

  return nextDue.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
};

export const computeScreenerIsOverdue = (
  nextDueStr: string | null | undefined,
): boolean => {
  if (!nextDueStr || nextDueStr === "N/A") {
    // If there is no due date, it's missing entirely so it's technically overdue (off-track)
    return true;
  }

  const nextDue = new Date(nextDueStr);
  if (isNaN(nextDue.getTime())) {
    return true;
  }

  // Add 1 day to ensure it's overdue at the start of the next day
  return new Date() > nextDue;
};

// Create a dummy measure if missing completely
const createEmptyScreenerMeasure = (
  name: string,
  status: ClinicalMeasurement["status"] = "off-track",
  explanation: string = "Assessment is missing and overdue.",
): ClinicalMeasurement => ({
  measureName: name,
  status: status,
  statusExplanation: explanation,
  latestScore: 0,
  trendData: [],
  lastCompleted: "N/A",
  nextDue: "N/A",
  isOverdue: status === "off-track",
  subscales: [],
  subscaleTrendData: {},
  longitudinalData: [],
  rawData: [],
});

/**
 * Validates and updates the status of general screeners like PHQ-9 and GAD-7.
 */
export const computeScreenerStatus = (
  measure: ClinicalMeasurement | null,
  measureName: string,
  patientAge: PatientAgeInfo | null | undefined,
  diagnosisCodes: string | null | undefined,
  validDiagnosisCodes: string[],
): ClinicalMeasurement => {
  const isApplicable = isScreenerApplicable(
    patientAge,
    diagnosisCodes,
    validDiagnosisCodes,
  );

  if (!isApplicable) {
    if (
      measure &&
      measure.longitudinalData &&
      measure.longitudinalData.length > 0
    ) {
      return {
        ...measure,
        status: "not-applicable",
        statusExplanation:
          "Patient age or diagnosis code does not meet criteria for this assessment.",
        nextDue: "N/A",
        isOverdue: false,
      };
    }
    return createEmptyScreenerMeasure(
      measureName,
      "not-applicable",
      "Patient age or diagnosis code does not meet criteria for this assessment.",
    );
  }

  const hasData =
    measure && measure.longitudinalData && measure.longitudinalData.length > 0;

  if (!hasData || !measure) {
    return createEmptyScreenerMeasure(measureName);
  }

  // Check discontinuation policy: if last 2 scores are <= 4
  const trend = measure.trendData || [];
  if (trend.length >= 2) {
    const lastScore = trend[trend.length - 1];
    const secondLastScore = trend[trend.length - 2];

    if (lastScore <= 4 && secondLastScore <= 4) {
      return {
        ...measure,
        status: "not-applicable",
        statusExplanation:
          "Last two consecutive scores were ≤4. Assessment discontinued per policy.",
        nextDue: "N/A",
        isOverdue: false,
      };
    }
  }

  const lastCompleted = measure.lastCompleted;
  const nextDue = computeScreenerNextDueDate(
    lastCompleted,
    measure.latestScore,
  );
  const isOverdue = computeScreenerIsOverdue(nextDue);

  return {
    ...measure,
    status: isOverdue ? "off-track" : "on-track",
    statusExplanation: isOverdue
      ? `Assessment is overdue. It was due on ${nextDue}.`
      : `Assessment is up to date. Next due on ${nextDue}.`,
    nextDue,
    isOverdue,
  };
};
